import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Download, Printer, Trash2 } from "lucide-react";
import { FileButtons } from "./AdminFeeChecks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const downloadPdf = async (ff) => {
  try {
    const { data, headers } = await axios.get(`${API}/fact-finds/${ff.id}/pdf`, { withCredentials: true, responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "fact-find.pdf";
    const url = URL.createObjectURL(data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    toast.error("Couldn't create the PDF.");
  }
};
const STATUSES = [
  ["new", "New"],
  ["contacted", "Reviewed"],
  ["closed", "Closed"],
];

const SummaryTable = ({ ff }) => (
  <div className="mt-6 space-y-8" data-testid={`fact-find-detail-${ff.id}`}>
    {ff.summary.length === 0 && <p className="text-sm text-[#4A524D]">No answers were provided.</p>}
    {ff.summary.map((sec) => (
      <div key={sec.title}>
        <h4 className="font-serif text-xl text-[#111413]">{sec.title}</h4>
        <table className="mt-3 w-full table-fixed text-sm">
          <colgroup>
            <col className="w-2/5" />
            {ff.has_partner ? (
              <>
                <col className="w-[30%]" />
                <col className="w-[30%]" />
              </>
            ) : (
              <col />
            )}
          </colgroup>
          {ff.has_partner && sec.rows.some((r) => r.person) && (
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-[#8A8F8B]">
                <th />
                <th className="text-left py-1 pr-3 font-normal">You</th>
                <th className="text-left py-1 font-normal">Partner</th>
              </tr>
            </thead>
          )}
          <tbody>
            {sec.rows.map((r, i) => (
              <tr key={`${r.label}-${i}`} className="border-t border-black/5 align-top">
                <td className="py-2 pr-4 text-[#4A524D] break-words">{r.label}</td>
                {(r.person && ff.has_partner ? r.values : [r.values[0]]).map((v, j) => (
                  <td key={j} colSpan={!r.person && ff.has_partner ? 2 : 1} className={`py-2 pr-3 ${v === "Unknown" ? "italic text-[#A78558]" : "text-[#111413]"}`}>
                    {v || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ))}
  </div>
);

const FactFindCard = ({ ff, fmtDate, onStatus, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <article data-testid={`fact-find-card-${ff.id}`} className="border border-black/10 bg-white px-6 md:px-8 py-6 print:border-0">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl tracking-tight text-[#111413]">{ff.client_name}</h3>
          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#4A524D]">
            {ff.email && <a href={`mailto:${ff.email}`}>{ff.email}</a>}
            {ff.phone && <a href={`tel:${ff.phone}`}>{ff.phone}</a>}
            {ff.has_partner && <span className="text-[#A78558]">With partner</span>}
          </div>
        </div>
        <p className="text-xs text-[#4A524D]">{fmtDate(ff.created_at)}</p>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3 print:hidden">
        <button
          data-testid={`fact-find-toggle-${ff.id}`}
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1.5 bg-[#0A2333] px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-[#F9F8F4]"
        >
          {open ? "Hide answers" : "View answers"}
          <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
        {STATUSES.map(([s, label]) => (
          <button
            key={s}
            data-testid={`fact-find-status-${s}-${ff.id}`}
            onClick={() => onStatus(ff.id, s)}
            className={`border px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
              ff.status === s ? "border-[#A78558] bg-[#A78558]/10 text-[#A78558]" : "border-black/15 text-[#4A524D] hover:text-[#111413]"
            }`}
          >
            {label}
          </button>
        ))}
        {open && (
          <button data-testid={`fact-find-print-${ff.id}`} onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-[0.15em] text-[#0A2333]">
            <Printer size={13} /> Print
          </button>
        )}
        <button data-testid={`fact-find-pdf-${ff.id}`} onClick={() => downloadPdf(ff)} className="inline-flex items-center gap-1.5 border border-[#0A2333]/30 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-[#0A2333] transition-colors duration-300 hover:bg-[#0A2333] hover:text-[#F9F8F4]">
          <Download size={13} /> Download PDF
        </button>
        <button data-testid={`fact-find-delete-${ff.id}`} onClick={() => onDelete(ff.id)} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-[0.15em] text-[#A78558]">
          <Trash2 size={13} /> Delete
        </button>
      </div>
      <FileButtons kind="fact-finds" item={ff} />
      {open && <SummaryTable ff={ff} />}
    </article>
  );
};

export const AdminFactFinds = ({ fmtDate }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    axios.get(`${API}/fact-finds`, { withCredentials: true }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const onStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/fact-finds/${id}`, { status }, { withCredentials: true });
      setItems((l) => l.map((f) => (f.id === id ? { ...f, status } : f)));
    } catch {
      toast.error("Couldn't update that fact find.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this fact find permanently?")) return;
    try {
      await axios.delete(`${API}/fact-finds/${id}`, { withCredentials: true });
      setItems((l) => l.filter((f) => f.id !== id));
      toast.success("Fact find deleted.");
    } catch {
      toast.error("Couldn't delete that fact find.");
    }
  };

  return (
    <section className="mt-10" data-testid="admin-fact-finds">
      {items === null ? (
        <p className="text-sm text-[#4A524D]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#4A524D]" data-testid="admin-fact-finds-empty">No fact finds submitted yet.</p>
      ) : (
        <div className="space-y-6">
          {items.map((ff) => (
            <FactFindCard key={ff.id} ff={ff} fmtDate={fmtDate} onStatus={onStatus} onDelete={onDelete} />
          ))}
        </div>
      )}
    </section>
  );
};
